<template>
  <c-box fontSize="sm" :my="{ base: 'md', md: '3.5rem' }" mx="-15px" data-cy="listorders_container">
    <c-flex
      pr="15px"
      pl="15px"
      justify-content="space-between"
      align-items="center"
      mt="4xl"
      mb="xl"
      height="16"
    >
      <c-heading
        as="h1"
        size="xl"
        color="brand.purpleDark"
        font-weight="normal"
        data-cy="orders-list-title"
        >{{ $t("orders.heading") }}</c-heading
      >
    </c-flex>
    <c-box px="15px" overflow="hidden">
      <c-flex :my="{ base: 'xl', md: '4xl' }" align="center" flex="1">
        <c-icon name="search" size="2.25rem" mr="md" color="brand.purple" />
        <c-box width="100%" max-width="24rem" overflow="hidden">
          <c-input
            id="searchTerm"
            position="relative"
            px="0"
            mb="0.75rem"
            :is-full-width="true"
            background="transparent"
            border-radius="0"
            border-width="0"
            border-bottom-width="1px"
            border-color="transparent"
            border-bottom-color="brand.purple"
            focus-border-color="transparent"
            v-model.trim="searchTerm"
            autocomplete="off"
            :placeholder="$t('ordering.searchOrders.placeholder')"
            @search="handleSearchInput(searchTerm)"
            @keydown="handleSearchInput(searchTerm)"
            @keyup="handleSearchInput(searchTerm)"
            @blur="handleSearchInput(searchTerm)"
            :_hover="{ borderBottomColor: 'brand.purple' }"
            :_focus="{ borderBottomColor: 'brand.purple' }"
            :_active="{ borderBottomColor: 'brand.purple' }"
          />
        </c-box>
      </c-flex>
      <c-box
        gridColumnGap="10px"
        :display="{ base: 'none', lg: 'grid' }"
        alignItems="center"
        textDecoration="none"
        transitionProperty="background-color"
        transitionDuration="0.25s"
        gridTemplateColumns="repeat(23, 1fr)"
        borderBottom="1px"
        borderColor="gray.200"
        py="xs"
        grid-auto-rows="minmax(1.75rem, auto)"
      >
        <c-flex gridColumn="1/3">
          <c-box overflow="hidden" white-space="nowrap" text-overflow="ellipsis">{{
            $t("ordering.timestamp")
          }}</c-box>
        </c-flex>

        <c-flex gridColumn="3/6">
          <c-box overflow="hidden" white-space="nowrap" text-overflow="ellipsis">{{
            $t("ordering.orderId")
          }}</c-box>
        </c-flex>

        <c-flex gridColumn="6/13">
          <c-box overflow="hidden" white-space="nowrap" text-overflow="ellipsis">{{
            $t("ordering.deliveryDetails.customerReference")
          }}</c-box>
        </c-flex>

        <c-flex gridColumn="13/15">
          <c-box overflow="hidden" white-space="nowrap" text-overflow="ellipsis">{{
            $t("ordering.projectDetails.projectId")
          }}</c-box>
        </c-flex>

        <c-flex gridColumn="15/17">
          <c-box overflow="hidden" white-space="nowrap" text-overflow="ellipsis">{{
            $t("shoppingCart.heading.totalPrice")
          }}</c-box>
        </c-flex>

        <c-flex gridColumn="17/19">
          <c-box overflow="hidden" white-space="nowrap" text-overflow="ellipsis">{{
            $t("ordering.orderStatus")
          }}</c-box>
        </c-flex>

        <c-flex gridColumn="19/24">
          <c-box overflow="hidden" white-space="nowrap" text-overflow="ellipsis">&nbsp;</c-box>
        </c-flex>
      </c-box>
      <c-box
        v-for="order in filteredOrders"
        :display="{ base: 'block', lg: 'grid' }"
        gridColumnGap="10px"
        cursor="pointer"
        alignItems="center"
        textDecoration="none"
        gridTemplateColumns="repeat(23, 1fr)"
        borderBottom="1px solid"
        borderColor="gray.300"
        py="xl"
        borderRadius="sm"
        grid-auto-rows="minmax(40px, auto)"
        :key="order._id"
        :data-cy="`order_row_container_${order.orderNumber ? order.orderNumber : order._id}`"
        tabindex="0"
        @click="openOrder(order)"
        @keydown.enter="openOrder(order)"
      >
        <c-flex justify-content="space-between" gridColumn="1/3">
          <c-box
            overflow="hidden"
            white-space="nowrap"
            text-overflow="ellipsis"
            :display="{ base: 'block', lg: 'none' }"
            :fontWeight="{ base: 'bold', lg: 'normal' }"
            >{{ $t("ordering.timestamp") }}</c-box
          >
          {{ order.timestamp | moment("D.M.YYYY") }}
        </c-flex>
        <c-flex justify-content="space-between" gridColumn="3/6">
          <c-box
            overflow="hidden"
            white-space="nowrap"
            text-overflow="ellipsis"
            :fontWeight="{ base: 'bold', lg: 'normal' }"
            :display="{ base: 'block', lg: 'none' }"
            >{{ $t("ordering.orderId") }}</c-box
          >
          {{ order.orderNumber }}
        </c-flex>
        <c-flex justify-content="space-between" gridColumn="6/13">
          <c-box
            overflow="hidden"
            white-space="nowrap"
            text-overflow="ellipsis"
            :fontWeight="{ base: 'bold', lg: 'normal' }"
            :display="{ base: 'block', lg: 'none' }"
            >{{ $t("ordering.deliveryDetails.customerReference") }}</c-box
          >
          {{ order.deliveryDetails.reference }}
        </c-flex>
        <c-flex justify-content="space-between" gridColumn="13/15">
          <c-box
            overflow="hidden"
            white-space="nowrap"
            text-overflow="ellipsis"
            :fontWeight="{ base: 'bold', lg: 'normal' }"
            :display="{ base: 'block', lg: 'none' }"
            >{{ $t("ordering.projectDetails.projectId") }}</c-box
          >
          {{ order.discounts ? order.discounts.projectDiscountCode : "" }}
        </c-flex>

        <c-flex justify-content="space-between" gridColumn="15/17">
          <c-box
            overflow="hidden"
            white-space="nowrap"
            text-overflow="ellipsis"
            :fontWeight="{ base: 'bold', lg: 'normal' }"
            :display="{ base: 'block', lg: 'none' }"
            >{{ $t("shoppingCart.heading.totalPrice") }}</c-box
          >
          <ShoppingCartTotalPrice :order="order" />
        </c-flex>

        <c-flex justify-content="space-between" gridColumn="17/19">
          <c-flex
            justify-content="space-between"
            width="100%"
            v-if="order.orderStatus === 'DRAFT'"
            @click.stop
          >
            <c-box
              overflow="hidden"
              white-space="nowrap"
              text-overflow="ellipsis"
              :fontWeight="{ base: 'bold', lg: 'normal' }"
              :display="{ base: 'block', lg: 'none' }"
              >{{ $t("ordering.orderStatus") }}</c-box
            >
            {{ $t("ordering.orderStatuses." + order.orderStatus) }}
          </c-flex>
          <c-box
            display="flex"
            justify-content="space-between"
            width="100%"
            v-chakra="{
              '.order-progress > div': {
                backgroundColor: 'brand.purple',
              },
              '.SHIPPED > div': {
                backgroundColor: 'brand.green',
              },
              '.CANCELLED > div': {
                backgroundColor: 'brand.redLight',
              },
              '.FAIL > div': {
                backgroundColor: 'brand.purpleLight',
              },
            }"
            v-else
          >
            <c-box
              :display="{ base: 'block', lg: 'none' }"
              :fontWeight="{ base: 'bold', lg: 'normal' }"
              >{{ $t("ordering.orderStatus") }}</c-box
            >
            <c-progress
              :width="{ base: '50%', lg: '100%' }"
              :value="orderProgress[order.orderStatus]"
              :max="100"
              :class="'order-progress ' + order.orderStatus"
              border-radius="full"
              size="lg"
              background="gray.200"
            ></c-progress>
          </c-box>
        </c-flex>
        <c-flex
          justify-content="space-between"
          gridColumn="19/23"
          v-if="order.orderStatus !== 'DRAFT'"
        >
          <c-box
            overflow="hidden"
            white-space="nowrap"
            text-overflow="ellipsis"
            :fontWeight="{ base: 'bold', lg: 'normal' }"
            :display="{ base: 'block', lg: 'none' }"
            >&nbsp;</c-box
          >
          {{ $t("ordering.orderStatuses." + order.orderStatus) }}
        </c-flex>
        <c-flex
          :justify-content="{ base: 'space-between', lg: 'space-evenly' }"
          alignItems="center"
          gridColumn="19/24"
          textAlign="center"
          v-if="order.orderStatus === 'DRAFT'"
          @click.stop
        >
          <c-text as="span" max-width="85%">
            <c-button
              m="0 auto"
              variation="solid"
              maxWidth="100%"
              background="brand.beige"
              border-color="brand.beige"
              fontSize="sm"
              @click="openContinueFromDraftDialog(order)"
              data-cy="continueFromDraftButton"
            >
              {{ $t("ordering.buttons.continueFromDraft") }}
            </c-button>
          </c-text>

          <c-text as="span">
            <c-link
              href="#"
              :data-cy="`deleteLink_${order._id}`"
              @click="openDeleteDraftDialog(order)"
            >
              <c-icon name="trashcan" size="2rem" color="brand.purple" />
            </c-link>
          </c-text>
        </c-flex>
      </c-box>
    </c-box>
    <dialog-modal
      :show-dialog="showContinueFromDraftDialog"
      @close="continueFromDraftDialogHandled(false)"
      content-ref="continue-from-draft-dialog"
    >
      <template v-slot:title>
        <c-text as="span" data-cy="continue-from-draft-dialog-title">
          {{ $t("ordering.buttons.continueFromDraft") }}
        </c-text>
      </template>
      <template v-slot:content>
        <c-text>{{ $t("ordering.continueFromDraft") }}</c-text>
      </template>
      <template v-slot:actions>
        <c-button
          variation="solid"
          @click="continueFromDraftDialogHandled(true)"
          background="brand.beige"
          borderColor="brand.beige"
          mr="md"
          data-cy="continue-from-draft-button"
        >
          {{ $t("common.yesButton") }}
        </c-button>
        <c-button @click="continueFromDraftDialogHandled(false)">
          {{ $t("common.noButton") }}
        </c-button>
      </template>
    </dialog-modal>

    <dialog-modal
      :show-dialog="showDeleteDraftDialog"
      @close="continueFromDraftDeleteHandled(false)"
      content-ref="delete-draft-dialog"
    >
      <template v-slot:title>
        <c-text as="span" data-cy="delete-draft-dialog-title">
          {{ $t("ordering.buttons.deleteDraft") }}
        </c-text>
      </template>
      <template v-slot:content>
        <c-text>{{ $t("ordering.ensureDeleteDraft") }}</c-text>
      </template>
      <template v-slot:actions>
        <c-button
          variation="solid"
          @click="continueFromDraftDeleteHandled(true)"
          background="brand.beige"
          borderColor="brand.beige"
          mr="md"
          data-cy="delete-draft-button"
        >
          {{ $t("common.yesButton") }}
        </c-button>
        <c-button @click="continueFromDraftDeleteHandled(false)">
          {{ $t("common.noButton") }}
        </c-button>
      </template>
    </dialog-modal>
  </c-box>
</template>

<script lang="ts">
import Modal from "@/components/Modal.vue";
import { progressPercentageByOrderStatus } from "@/constants";
import Icon from "@/design-system/elements/Icon.vue";
import Price from "@/design-system/elements/Price.vue";
import Search from "@/design-system/elements/Search.vue";
import {
  CBox,
  CButton,
  CFlex,
  CHeading,
  CIcon,
  CInput,
  CLink,
  CProgress,
  CText,
} from "@chakra-ui/vue";
import moment from "moment-timezone";
import { defineComponent } from "vue";
import { mapState } from "vuex";
import ShoppingCartTotalPrice from "./ShoppingCartTotalPrice.vue";
import { IOrder } from "@iloq/shared/IOrder";

export default defineComponent({
  name: "ListOrders",
  components: {
    "dialog-modal": Modal,
    Search,
    Price,
    Icon,
    ShoppingCartTotalPrice,
    CIcon,
    CInput,
    CHeading,
    CFlex,
    CBox,
    CButton,
    CText,
    CProgress,
    CLink,
  },
  computed: {
    ...mapState({
      orders: (state) => state.orders.orders,
    }),
    filteredOrders() {
      if (this.search) {
        return this.orders.filter(
          (item) =>
            [
              `${item.orderNumber || ""}`,
              `${moment(item.timestamp).format("D.M.YYYY")}`,
              `${(item.deliveryDetails && item.deliveryDetails.reference) || ""}`,
              `${(item.discounts && item.discounts.projectDiscountCode) || ""}`,
            ]
              .join(" ")
              .indexOf(this.search.trim()) >= 0
        );
      }
      return this.orders;
    },
  },
  data() {
    return {
      searchTerm: "",
      orderProgress: progressPercentageByOrderStatus,
      search: "",
      showDeleteDraftDialog: false,
      showContinueFromDraftDialog: false,
      selectedOrder: undefined as IOrder | undefined,
      orderToBeRemoved: undefined as IOrder | undefined,
    };
  },
  methods: {
    handleSearchInput(keyword) {
      this.search = keyword;
    },
    openContinueFromDraftDialog(order) {
      this.selectedOrder = order;
      this.showContinueFromDraftDialog = true;
    },
    openOrder(order) {
      this.$router.push({
        name: "view_order",
        params: {
          id: order.orderStatus !== "DRAFT" ? order.orderNumber || "" : order._id || "",
          isConfirmationForNewOrder: "false",
        },
      });
    },
    continueFromDraft() {
      this.$store
        .dispatch("productSearch/ensureProductsExistWithLatestDataAndPrice", {
          rows: this.selectedOrder?.rows,
          failWhenNoProducts: true,
        })
        .then(async (orderRowsWithFreshProducts) => {
          await this.$store.dispatch("shoppingCart/continueOrderFromDraft", {
            ...this.selectedOrder,
            rows: orderRowsWithFreshProducts,
          });
          this.$router.push({ path: "/shopping_cart" });
        })
        .catch(() => {
          this.$store.commit("showGeneralError", {
            dialogTitle: this.$t("ordering.continueFromDraftError.title"),
            dialogText: this.$t("ordering.continueFromDraftError.text"),
          });
        });
    },
    continueFromDraftDialogHandled(success) {
      this.showContinueFromDraftDialog = false;
      if (success) {
        this.continueFromDraft();
      }
    },
    openDeleteDraftDialog(order) {
      this.orderToBeRemoved = order;
      this.showDeleteDraftDialog = true;
    },
    continueFromDraftDeleteHandled(success) {
      this.showDeleteDraftDialog = false;
      if (success && this.orderToBeRemoved) {
        this.deleteDraft();
      }
    },
    deleteDraft() {
      this.$store
        .dispatch("orders/deleteOrder", this.orderToBeRemoved)
        .then(() => {
          this.$store.dispatch("orders/fetchOrders");
        })
        .catch(() => {
          this.$store.commit("showGeneralError", {
            dialogTitle: this.$t("ordering.deleteDraftError.title"),
            dialogText: this.$t("ordering.deleteDraftError.text"),
          });
        });
    },
  },
  beforeMount() {
    this.$store.dispatch("orders/fetchOrders", { root: true });
  },
});
</script>
